// The one live channel: a WebSocket from each device to its list's Durable
// Object (Hibernation API). Changes travel both ways in the same shape the
// store and the DO apply, so sync (stage 3) adds the outbox replay and
// nothing else. A device says the last sequence it saw; the DO answers with
// what it missed, or the whole list when it is too far behind.
//
// Stage 3 added two things, both optional to the other side, so an older
// device or an older list still talks to a newer one: `seen`, a device
// saying how far it has applied (the list keeps its change log for the
// devices still behind), and an id and a retry flag on `error`, so a device
// can tell which change was refused and whether to send it again.
import type { RunProgress } from './artifacts';
import type { Change } from './edit';
import type { Project } from './plan';

/** A change as the list recorded it: its place in the list's order. */
export type Logged = Change & { seq: number };

export type ClientMessage =
  | { type: 'hello'; lastSeq: number }
  | { type: 'change'; change: Change }
  /** This device has applied everything up to `seq`: sent a moment after a snapshot or changes land, so the list can prune its log behind it. */
  | { type: 'seen'; seq: number };

export type ServerMessage =
  /** Everything, when the device is new or further behind than the log keeps. */
  | { type: 'snapshot'; seq: number; projects: Project[]; runs: RunProgress[] }
  /** What changed since the device's last sequence, in order. */
  | { type: 'changes'; changes: Logged[] }
  /** A change from this device landed, at this sequence (or was already there). */
  | { type: 'ack'; id: string; seq: number }
  | { type: 'run'; run: RunProgress }
  /**
   * A message refused, in plain words. `id` names the change refused, when
   * the message was one; `retry` says the refusal will pass (too many changes
   * this hour, or a fault of ours), so the device keeps the change and sends
   * it again later. Without `retry`, the change will never be taken as it is.
   */
  | { type: 'error'; message: string; id?: string; retry?: boolean };

export const SOCKET_PATH = (listId: string) => `/api/lists/${encodeURIComponent(listId)}/socket`;

/**
 * How the list closes a socket on purpose. `signedOut`: the session ended
 * (signed out here or everywhere, or the account was deleted), so the device
 * clears itself. `suspended`: the account is paused by us, so the device
 * stops syncing and keeps everything it holds.
 */
export const CLOSE_CODES = { signedOut: 4001, suspended: 4003 } as const;
